'use client'

import { useState } from 'react'
import { Loader2, ShieldCheck, ExternalLink, RefreshCw, Smartphone, Copy } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from 'sonner'
import type { DiditAction } from '@/lib/didit/didit-policies'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: DiditAction
  onApproved: () => void | Promise<void>
}

export default function DiditKycDialog({ open, onOpenChange, action, onApproved }: Props) {
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(false)
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)

  async function begin() {
    setBusy(true)
    try {
      const res = await fetch('/api/kyc/didit/session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.ok) {
        const messages: Record<string, string> = {
          DIDIT_AUTH_FAILED: 'Didit rechazó la API Key. Revisa las credenciales del propietario.',
          DIDIT_BALANCE_REQUIRED: 'La cuenta Didit de esta comunidad no tiene saldo suficiente.',
          DIDIT_WORKFLOW_OR_REQUEST_INVALID: 'Didit rechazó el Workflow ID o los parámetros de la sesión. Revisa la configuración.',
          DIDIT_RATE_LIMIT: 'Didit está limitando temporalmente las solicitudes. Intenta nuevamente.',
          DIDIT_PROVIDER_ERROR: 'Didit no pudo crear la sesión. Revisa los logs del servidor.',
          DIDIT_INVALID_RESPONSE: 'Didit devolvió una respuesta inesperada. Revisa los logs del servidor.',
        }
        throw new Error(messages[data?.error] || data?.error || 'No se pudo comenzar la verificación')
      }
      if (data.required === false || data.status === 'approved') {
        await onApproved()
        onOpenChange(false)
        return
      }
      if (typeof data.url !== 'string') throw new Error('Didit no devolvió una URL de verificación')
      setVerificationUrl(data.url)
      setStatus('pending')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error iniciando verificación')
    } finally {
      setBusy(false)
    }
  }

  async function check() {
    setChecking(true)
    try {
      const res = await fetch(`/api/kyc/didit/status?action=${encodeURIComponent(action)}`, { cache: 'no-store' })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'No se pudo consultar Didit')
      setStatus(data.status)
      if (data.allowed) {
        toast.success('Identidad verificada')
        await onApproved()
        onOpenChange(false)
      } else if (data.status === 'declined') {
        toast.error('Didit rechazó la verificación')
      } else {
        toast.info('La verificación aún no está aprobada')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error consultando verificación')
    } finally {
      setChecking(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl border bg-muted/30"><ShieldCheck className="h-6 w-6" /></div>
          <DialogTitle>Verifica tu identidad</DialogTitle>
          <DialogDescription>Esta comunidad requiere verificación de identidad antes de continuar. El proceso se realiza de forma segura en Didit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          {!verificationUrl ? (
            <Button className="w-full gap-2" onClick={begin} disabled={busy || checking}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Comenzar verificación
            </Button>
          ) : (
            <div className="space-y-3 rounded-2xl border bg-muted/20 p-4">
              <div className="flex items-center justify-center gap-2 text-sm font-medium"><Smartphone className="h-4 w-4" /> Escanea con tu teléfono</div>
              <div className="mx-auto w-fit rounded-xl bg-white p-3"><QRCodeSVG value={verificationUrl} size={210} marginSize={0} /></div>
              <p className="text-center text-xs text-muted-foreground">Escanea el código con la cámara de tu teléfono y completa tu identificación en Didit.</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="outline" size="sm" onClick={() => { void navigator.clipboard.writeText(verificationUrl).then(() => toast.success('Enlace copiado')).catch(() => toast.error('No se pudo copiar')) }}><Copy className="mr-2 h-4 w-4" /> Copiar enlace</Button>
                <Button size="sm" asChild><a href={verificationUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" /> Abrir Didit</a></Button>
              </div>
            </div>
          )}
          <Button className="w-full gap-2" variant="outline" onClick={check} disabled={busy || checking}>
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Ya terminé, comprobar verificación
          </Button>
          {status && <p className="text-center text-xs text-muted-foreground">Estado: {status}</p>}
          <p className="text-center text-xs text-muted-foreground">Una vez aprobada tu identidad, podrás continuar. No necesitas volver a verificarte para otras funciones de esta misma comunidad.</p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
